import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Hook to trigger shake animation on elements
 * Returns isShaking state and triggerShake function
 *
 * @example
 * const { isShaking, triggerShake } = useShake();
 * <div className={isShaking ? 'animate-shake' : ''}>...</div>
 */
export function useShake(duration = 500) {
  const [isShaking, setIsShaking] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerShake = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsShaking(true);

    timeoutRef.current = setTimeout(() => {
      setIsShaking(false);
    }, duration);
  }, [duration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { isShaking, triggerShake };
}
